import type { ReactNode } from 'react'
import type { ConversationTreeRow, MiniappConversation, SourceItem } from './chat-types'

export const SHORT_LAW_NAMES: Record<string, string> = {
  GKRF: 'ГК РФ', SKRF: 'СК РФ', TKRF: 'ТК РФ',
  NKRF: 'НК РФ',
  UKRF: 'УК РФ', ZKRF: 'ЖК РФ', KOAPRF: 'КоАП РФ',
  KASRF: 'КАС РФ', GRADKRF: 'ГрК РФ', GPKRF: 'ГПК РФ',
  APKRF: 'АПК РФ', UPKRF: 'УПК РФ', UIKRF: 'УИК РФ',
  ZEMKRF: 'ЗК РФ', VODKRF: 'ВК РФ', LESKRF: 'ЛК РФ',
  VOZDKRF: 'ВозК РФ', BUDKRF: 'БК РФ', '44FZ': '44-ФЗ',
  KVVTRF: 'КВВТ РФ', KTMRF: 'КТМ РФ',
}

const BLOCKED_WEB_SOURCE_DOMAINS = new Set([
  'hh.ru',
  'superjob.ru',
  'rabota.ru',
  'zarplata.ru',
  'career.habr.com',
  'trudvsem.ru',
  'youla.ru',
  'avito.ru',
])
const WEB_SOURCE_MIN_RELEVANCE_SCORE = 1.25
const HARD_BLOCK_WEB_SOURCE_DOMAINS = String(
  import.meta.env.VITE_HARD_BLOCK_WEB_SOURCE_DOMAINS || '',
).trim().toLowerCase() === 'true'

function extractDomain(value: string): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  try {
    const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`)
    return parsed.hostname.toLowerCase().replace(/^\.+/, '')
  } catch {
    return ''
  }
}

function isBlockedDomain(domain: string): boolean {
  const host = extractDomain(domain)
  if (!host) return false
  for (const blocked of BLOCKED_WEB_SOURCE_DOMAINS) {
    if (host === blocked || host.endsWith(`.${blocked}`)) return true
  }
  return false
}

export function buildConversationTreeRows<T extends {
  id: string
  updated_at?: string
  branch_parent_conversation_id?: string | null
}>(
  items: T[],
  collapsedNodeIds: ReadonlySet<string>,
): Array<ConversationTreeRow<T>> {
  if (items.length <= 1) {
    return items.map((conversation) => ({
      conversation,
      depth: 0,
      hasChildren: false,
      isCollapsed: false,
      isLastSibling: true,
      ancestorHasNext: [],
    }))
  }

  const parseUpdatedAt = (value?: string): number => {
    if (!value) return 0
    const ts = Date.parse(value)
    return Number.isFinite(ts) ? ts : 0
  }

  const sortByUpdatedDesc = (left: T, right: T): number =>
    parseUpdatedAt(right.updated_at) - parseUpdatedAt(left.updated_at)

  const byId = new Map<string, T>()
  const childrenByParent = new Map<string, T[]>()
  const roots: T[] = []

  for (const item of items) {
    byId.set(item.id, item)
  }

  for (const item of items) {
    const parentId = String(item.branch_parent_conversation_id || '').trim()
    if (parentId && parentId !== item.id && byId.has(parentId)) {
      const children = childrenByParent.get(parentId) || []
      children.push(item)
      childrenByParent.set(parentId, children)
    } else {
      roots.push(item)
    }
  }

  roots.sort(sortByUpdatedDesc)
  for (const children of childrenByParent.values()) {
    children.sort(sortByUpdatedDesc)
  }

  const rows: Array<ConversationTreeRow<T>> = []
  const visited = new Set<string>()

  const walk = (item: T, depth: number, ancestorHasNext: boolean[], isLastSibling: boolean) => {
    if (visited.has(item.id)) return
    visited.add(item.id)
    const children = childrenByParent.get(item.id) || []
    const hasChildren = children.length > 0
    const isCollapsed = hasChildren && collapsedNodeIds.has(item.id)
    rows.push({
      conversation: item,
      depth,
      hasChildren,
      isCollapsed,
      isLastSibling,
      ancestorHasNext,
    })
    if (isCollapsed) return
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index]
      const childIsLast = index === children.length - 1
      walk(child, depth + 1, [...ancestorHasNext, !childIsLast], childIsLast)
    }
  }

  for (let index = 0; index < roots.length; index += 1) {
    const root = roots[index]
    const isLastRoot = index === roots.length - 1
    walk(root, 0, [], isLastRoot)
  }

  if (rows.length < items.length) {
    const leftovers = items.filter((item) => !visited.has(item.id)).sort(sortByUpdatedDesc)
    for (let index = 0; index < leftovers.length; index += 1) {
      const item = leftovers[index]
      const isLast = index === leftovers.length - 1
      walk(item, 0, [], isLast)
    }
  }

  return rows
}

// Убрать «сырые» инлайновые маркеры цитат, просачивающиеся в текст ответа модели, напр.
// 【mcp_law_backend_law_get_article://NKRF/210】. Ссылки на нормы показываются чипами «Источники».
export function stripInlineCitationMarkers(text: string): string {
  if (!text) return text
  return text
    .replace(/【[^】]*】/g, '')
    .replace(/[ \t]+([),.;:!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
}

export function renderCitationText(text: string): ReactNode {
  const regex = /<<(.*?)>>/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index))
    parts.push(<mark key={match.index} className="bg-telegram-button/20 text-telegram-text px-0.5 rounded" style={{ color: 'inherit' }}>{match[1]}</mark>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) parts.push(text.substring(lastIndex))
  return parts.length > 0 ? parts : text
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

export function normalizeWebSourceLinks(sources?: SourceItem[]): Array<{
  key: string
  url: string
  title: string
}> {
  if (!Array.isArray(sources) || sources.length === 0) return []
  const links: Array<{ key: string; url: string; title: string }> = []
  const seen = new Set<string>()
  for (const source of sources) {
    const metadata = asRecord(source.metadata) || {}
    const url = String(source.url || metadata.url || '').trim()
    if (!/^https?:\/\//i.test(url)) continue

    const docType = String(source.doc_type || metadata.doc_type || source.source || metadata.source || '').trim().toLowerCase()
    const origin = String(metadata.origin || '').trim().toLowerCase()
    const alwaysKeep = origin === 'user_query_url' || origin === 'project_link'
    const relevanceKeep = metadata.relevance_keep
    const relevanceScore = Number(metadata.relevance_score)
    const domain = String(source.domain || metadata.domain || '').trim() || extractDomain(url)
    if (!alwaysKeep && relevanceKeep === false) continue
    if (!alwaysKeep && Number.isFinite(relevanceScore) && relevanceScore < WEB_SOURCE_MIN_RELEVANCE_SCORE) continue
    if (!alwaysKeep && HARD_BLOCK_WEB_SOURCE_DOMAINS && isBlockedDomain(domain)) continue

    const lawCode = String(source.law_code || metadata.law_code || '').trim()

    const looksLawSource = Boolean(
      lawCode
      || docType.startsWith('law')
      || docType.includes('mcp')
      || docType.includes('qdrant'),
    )
    const looksWebSource = Boolean(
      docType.startsWith('web')
      || docType.includes('url')
      || docType.includes('site')
      || docType.includes('link')
      || origin === 'web'
      || origin === 'user_query_url'
      || !looksLawSource,
    )
    if (!looksWebSource) continue

    const title = String(
      source.title
      || source.filename
      || metadata.title
      || metadata.filename
      || domain
      || url,
    ).trim() || url
    const key = `${url}|${title}`
    if (seen.has(key)) continue
    seen.add(key)
    links.push({ key, url, title })
    if (links.length >= 8) break
  }
  return links
}

export function formatSpaceLabel(spacesItems: Array<{ id?: string; name?: string }>, selectedSpaceId?: string): string {
  const current = spacesItems.find((space) => space.id === selectedSpaceId)
  return current?.name || 'Проект'
}

export type { MiniappConversation }
